import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

/**
 * V3 Dark-Navy SaaS visual primitives.
 * Kept intentionally terse — 80% visual, 20% text.
 */

/** Ambient page hero — eyebrow + title + optional action slot */
export function PageHero({ eyebrow, title, sub, right, children, testId }) {
  return (
    <div className="page-hero" data-testid={testId}>
      <div className="flex flex-wrap items-start justify-between gap-4 relative z-10">
        <div>
          {eyebrow && <div className="eyebrow eyebrow-brand mb-2">{eyebrow}</div>}
          <h1 className="text-3xl sm:text-4xl font-outfit font-bold txt-hi tracking-tight">{title}</h1>
          {sub && <div className="txt-md font-manrope text-sm mt-1">{sub}</div>}
        </div>
        {right}
      </div>
      {children && <div className="relative z-10 mt-4">{children}</div>}
    </div>
  );
}

/** Hero stat — big number, icon chip, tiny label */
export function StatHero({ label, value, icon: Icon, tone = "brand", trend, sub, testId, to }) {
  const Card = to ? Link : "div";
  return (
    <Card to={to} className={`stat-hero card-dark-hover block ${to ? "cursor-pointer" : ""}`} data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className={`icon-wrap icon-${tone}`}>{Icon && <Icon className="w-5 h-5" strokeWidth={2} />}</div>
        {trend != null && (
          <span className={`spill ${trend >= 0 ? "spill-active" : "spill-emergency"}`}>
            <span className="dot" /> {trend >= 0 ? "+" : ""}{trend}%
          </span>
        )}
      </div>
      <div className="num mt-4">{value}</div>
      <div className="eyebrow mt-2">{label}</div>
      {sub && <div className="txt-lo text-xs font-manrope mt-2">{sub}</div>}
    </Card>
  );
}

/** Action tile — icon + big label + arrow */
export function ActionTile({ to, icon: Icon, label, sub, tone = "brand", testId }) {
  return (
    <Link to={to} className="card-dark card-dark-hover p-5 block group" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className={`icon-wrap icon-${tone}`}>{Icon && <Icon className="w-5 h-5" strokeWidth={2} />}</div>
        <ArrowUpRight className="w-4 h-4 txt-lo group-hover:txt-hi transition-colors" />
      </div>
      <div className="font-outfit font-bold txt-hi text-lg mt-4">{label}</div>
      {sub && <div className="txt-lo text-xs font-manrope mt-1">{sub}</div>}
    </Link>
  );
}

/** Status pill wrapper — canonical for LIVE / EMERGENCY / ACTIVE / STANDBY / MANAGED / TRACKED / MATCH */
export function StatusPill({ kind = "neutral", children, testId }) {
  return (
    <span className={`spill spill-${kind}`} data-testid={testId}>
      <span className="dot" /> {children}
    </span>
  );
}

/** SVG progress ring (0–100) */
export function ProgressRing({ value = 0, size = 88, stroke = 8, label, sub }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  const off = c - (pct / 100) * c;
  return (
    <div className="inline-flex items-center gap-3">
      <svg width={size} height={size} className="-rotate-90">
        <defs>
          <linearGradient id="ringGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#22D3EE" />
            <stop offset="100%" stopColor="#0066FF" />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} className="ring-track" fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} className="ring-fill"
          fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
        />
      </svg>
      {label && (
        <div>
          <div className="font-outfit font-bold text-xl txt-hi leading-none">{label}</div>
          {sub && <div className="eyebrow mt-1">{sub}</div>}
        </div>
      )}
    </div>
  );
}

/** Compact sparkline */
export function Sparkline({ points = [], width = 120, height = 34, testId }) {
  if (!points.length) return null;
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const range = Math.max(max - min, 1);
  const step = width / Math.max(points.length - 1, 1);
  const path = points.map((v, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${(height - ((v - min) / range) * height).toFixed(1)}`).join(" ");
  const fill = `${path} L${width},${height} L0,${height} Z`;
  return (
    <svg width={width} height={height} data-testid={testId}>
      <defs>
        <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#60A5FA" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#60A5FA" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={fill} className="spark-fill" />
      <path d={path} className="spark-line" />
    </svg>
  );
}

/** Row of icon-first metrics — used on Ops Room, booking detail etc. */
export function MetricRow({ items }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {items.map((it, i) => {
        const Icon = it.icon;
        return (
          <div key={i} className="card-inner p-3 flex items-center gap-3" data-testid={it.testId}>
            {Icon && <Icon className="w-4 h-4 text-brand" strokeWidth={2} />}
            <div>
              <div className="eyebrow">{it.label}</div>
              <div className="font-outfit font-semibold txt-hi text-sm">{it.value}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
